import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import { AuthContext } from './Provider.js';
import {
  FaRoute,
  FaLocationArrow,
  FaSearchLocation,
  FaUser,
  FaBox,
  FaCalendarAlt,
  FaClock,
  FaRuler,
  FaCarSide,
  FaExclamationTriangle,
  FaSlidersH
} from 'react-icons/fa';
import Spinner from './common/Spinner';
import { Checkbox1 } from './Templates';
import './RouteMatches.css';

/**
 * Component for searching and displaying route matches
 * 
 * @param {Object} props
 * @param {number|string} props.routeId - ID of the route to find matches for
 * @param {boolean} props.isPackage - Whether the route is for a package
 * @param {boolean} props.isGroupRide - Whether the route is for a group ride
 * @param {string} props.role - Filter by role (Driver, Passenger, Package)
 * @param {number} props.radiusKm - Search radius in kilometers
 * @param {boolean} props.showAddressInsteadOfCoords - Whether to show actual addresses instead of coordinates
 */
export default function RouteMatches({ routeId, isPackage = false, isGroupRide = false, role = null, radiusKm = 10, onSelectMatch, showAddressInsteadOfCoords = false, onRadiusChange }) {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchRadius, setSearchRadius] = useState(radiusKm);
  const [addressData, setAddressData] = useState({});
  const [maxAllowedRadius, setMaxAllowedRadius] = useState(5); // Default to Free plan radius
  
  // Filter states
  const [filters, setFilters] = useState({
    showDrivers: true,
    showPassengers: true,
    showPackages: true
  });
  
  const navigate = useNavigate();

  // Check user's subscription for max allowed route matching radius
  useEffect(() => {
    if (!user) return;
    
    const checkSubscriptionLimits = async () => {
      try {
        const { data: limitData, error: limitError } = await supabase.rpc('check_subscription_limit', {
          user_id: user.id,
          feature_name: 'route_match_radius_km'
        });
        
        if (limitError) {
          console.error('Error checking subscription limits:', limitError);
          return;
        }
        
        if (limitData?.value) {
          const radius = parseInt(limitData.value, 10);
          setMaxAllowedRadius(radius);
          
          // If current radius exceeds allowed limit, reset to max allowed
          if (searchRadius > radius) {
            setSearchRadius(radius);
            addToast({
              type: 'info',
              title: t('subscription.limit_applied') || 'Subscription Limit Applied',
              description: `Your current plan allows maximum ${radius}km matching radius.`
            });
          }
        }
      } catch (error) {
        console.error('Error checking subscription limits:', error);
      }
    };
    
    checkSubscriptionLimits();
  }, [user, addToast, t]);

  // Fetch route matches when component mounts or inputs change
  useEffect(() => {
    if (!routeId) {
      setLoading(false);
      return;
    }

    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);

        // Only proceed if we have a valid route ID
        if (!routeId && !isPackage && !isGroupRide) {
          console.log('No route ID provided');
          setMatches([]);
          setLoading(false);
          return;
        }

        // Call the find_route_matches function with appropriate typing for each parameter
        // Important: For route_id, we need to handle both integers and UUIDs
        let params = {};
        
        try {
          if (isPackage) {
            params = {
              p_route_id: null,
              p_is_package: true,
              p_package_id: routeId,
              p_group_ride_id: null,
              p_is_group_ride: false,
              p_radius_km: parseFloat(searchRadius) || 10,
              p_role: role
            };
          } else if (isGroupRide) {
            params = {
              p_route_id: null,
              p_is_package: false,
              p_package_id: null,
              p_group_ride_id: routeId,
              p_is_group_ride: true,
              p_radius_km: parseFloat(searchRadius) || 10,
              p_role: role
            };
          } else {
            // For posts, ensure ID is an integer and handle NaN
            const postId = parseInt(routeId, 10);
            if (isNaN(postId)) {
              throw new Error('Invalid post ID');
            }
            
            params = {
              p_route_id: postId,
              p_is_package: false,
              p_package_id: null,
              p_group_ride_id: null,
              p_is_group_ride: false,
              p_radius_km: parseFloat(searchRadius) || 10,
              p_role: role
            };
          }
          
          console.log('Calling find_route_matches with params:', params);
          
          // First check if route points exist for this post/package/group ride
          let routePointsExist = false;
          if (isPackage) {
            const { data: pointsData, error: pointsError } = await supabase
              .from('route_points')
              .select('id', { count: 'exact' })
              .eq('package_id', routeId)
              .limit(1);
              
            if (!pointsError && pointsData && pointsData.length > 0) {
              routePointsExist = true;
              console.log(`Found ${pointsData.length} route points for package ID ${routeId}`);
            }
          } else if (isGroupRide) {
            const { data: pointsData, error: pointsError } = await supabase
              .from('route_points')
              .select('id', { count: 'exact' })
              .eq('group_ride_id', routeId)
              .limit(1);
              
            if (!pointsError && pointsData && pointsData.length > 0) {
              routePointsExist = true;
              console.log(`Found ${pointsData.length} route points for group ride ID ${routeId}`);
            }
          } else {
            const { data: pointsData, error: pointsError } = await supabase
              .from('route_points')
              .select('id', { count: 'exact' })
              .eq('post_id', routeId)
              .limit(1);
              
            if (!pointsError && pointsData && pointsData.length > 0) {
              routePointsExist = true;
              console.log(`Found ${pointsData.length} route points for post ID ${routeId}`);
            }
          }
          
          if (!routePointsExist) {
            let entityType = 'post';
            if (isPackage) entityType = 'package';
            if (isGroupRide) entityType = 'group ride';
            console.warn(`No route points found for ${entityType} ID ${routeId}. Route matching will not work.`);
            setMatches([]);
            setLoading(false);
            addToast({
              type: 'warning',
              title: t('warnings.no_route_data'),
              description: t('warnings.route_matching_not_available') || 'Route matching not available for this post. Please recreate your post and calculate route.'
            });
            return;
          }
          
          const { data: matchesData, error: matchesError } = await supabase.rpc('find_route_matches', params);

          if (matchesError) {
            console.log('Route matches error:', matchesError, 'Details:', matchesError.details);
            throw matchesError;
          }

          // Filter out matches that belong to the current user
          const filteredMatches = (matchesData || []).filter(match => {
            return match.user_id !== user?.id;
          });
          
          console.log('Route matches found:', matchesData?.length || 0);
          console.log('Route matches (after user filtering):', filteredMatches.length);
          setMatches(filteredMatches);
          
        } catch (err) {
          console.error('Error fetching route matches:', err);
          setError(err.message || t('errors.matches_fetch_failed') || 'Failed to fetch route matches');
          addToast({
            type: 'error',
            title: t('errors.error'),
            description: err.message || t('errors.matches_fetch_failed')
          });
        } finally {
          setLoading(false);
        }
      } catch (outerErr) {
        console.error('Outer error in fetchMatches:', outerErr);
        setLoading(false);
        setError(outerErr.message || 'An unexpected error occurred');
      }
    };

    fetchMatches();
  }, [routeId, isPackage, isGroupRide, role, searchRadius, t, addToast]);
  
  // Effect to fetch address data for matches if showAddressInsteadOfCoords is true
  useEffect(() => {
    if (!showAddressInsteadOfCoords || !matches || matches.length === 0) return;
    
    const fetchAddressData = async () => {
      const newAddressData = {};
      
      for (const match of matches) {
        try {
          // Fetch the post or package data to get addresses
          const { data, error } = await supabase
            .from(match.match_type === 'post' ? 'posts' : 'packages')
            .select('address_from_street, address_from_city, address_to_street, address_to_city')
            .eq('id', match.match_id)
            .single();
          
          if (error) throw error;
          
          if (data) {
            // Format addresses
            const fromAddress = [data.address_from_street, data.address_from_city].filter(Boolean).join(', ');
            const toAddress = [data.address_to_street, data.address_to_city].filter(Boolean).join(', ');
            
            newAddressData[`${match.match_id}-from`] = fromAddress || `${parseFloat(match.start_point_lat || 0).toFixed(4)}, ${parseFloat(match.start_point_lng || 0).toFixed(4)}`;
            newAddressData[`${match.match_id}-to`] = toAddress || `${parseFloat(match.end_point_lat || 0).toFixed(4)}, ${parseFloat(match.end_point_lng || 0).toFixed(4)}`;
          }
        } catch (err) {
          console.error('Error fetching address data for match:', err);
          // Use coordinates as fallback
          newAddressData[`${match.match_id}-from`] = `${parseFloat(match.start_point_lat || 0).toFixed(4)}, ${parseFloat(match.start_point_lng || 0).toFixed(4)}`;
          newAddressData[`${match.match_id}-to`] = `${parseFloat(match.end_point_lat || 0).toFixed(4)}, ${parseFloat(match.end_point_lng || 0).toFixed(4)}`;
        }
      }
      
      setAddressData(newAddressData);
    };
    
    fetchAddressData();
  }, [matches, showAddressInsteadOfCoords]);
  
  // Apply filters to matches
  useEffect(() => {
    if (!matches || matches.length === 0) {
      setFilteredMatches([]);
      return;
    }
    
    const filtered = matches.filter(match => {
      const role = match.match_role;
      
      if (role === 'Driver' && !filters.showDrivers) return false;
      if (role === 'Passenger' && !filters.showPassengers) return false;
      if (role === 'Package' && !filters.showPackages) return false;
      
      return true;
    });
    
    setFilteredMatches(filtered);
    console.log('Applied filters:', filters, 'Filtered matches:', filtered.length, 'from', matches.length);
  }, [matches, filters]);

  // Function to update search radius
  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value, 10);
    
    // Check if new radius exceeds subscription limit
    if (newRadius > maxAllowedRadius) {
      addToast({
        type: 'warning',
        title: t('errors.limit_reached') || 'Subscription Limit',
        description: `Your current plan allows maximum ${maxAllowedRadius}km matching radius. Upgrade for wider search range!`
      });
      
      // Ask if they want to view subscription plans
      if (window.confirm(t('chat.view_subscription_plans') || 'Would you like to view our subscription plans?')) {
        navigate('/subscription-plans');
        return;
      }
      
      // Set to max allowed if user declines to view plans
      setSearchRadius(maxAllowedRadius);
      if (onRadiusChange) {
        onRadiusChange(maxAllowedRadius);
      }
      return;
    }
    
    // Proceed with the update if within limits
    setSearchRadius(newRadius);
    
    // Call the parent component's onRadiusChange function if provided
    if (onRadiusChange) {
      onRadiusChange(newRadius);
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };
  
  // Function to handle match selection
  const handleMatchClick = (match) => {
    // Open a full details modal directly via window event
    if (match && match.match_id && match.match_type) {
      console.log('RouteMatches dispatching openPostDetailsModal event:', match.match_id, match.match_type);
      
      // Dispatch custom event to open the modal
      const openModalEvent = new CustomEvent('openPostDetailsModal', {
        detail: {
          id: match.match_id,
          type: match.match_type
        }
      });
      window.dispatchEvent(openModalEvent);
    } else if (onSelectMatch) {
      // Fallback to original behavior if needed
      onSelectMatch(match);
    }
  };

  if (loading) {
    return (
      <div className="RM-route-matches-loading">
        <Spinner size="medium" customText={t('app.loading') || 'Loading matches...'} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="RM-route-matches-error">
        <FaExclamationTriangle />
        <p>{error}</p>
      </div>
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <div className="RM-route-matches-container">
        <div className="RM-route-matches-header">
          <h3>
            {t('matches.title') || 'Route Matches'}
          </h3>
          <div className="RM-route-matches-controls">
            <div className="RM-radius-control">
              <label>
                {t('matches.search_radius') || 'Search Radius'}:
                <select value={searchRadius} onChange={handleRadiusChange}>
                <option value="5">5 km</option>
                <option value="10" disabled={maxAllowedRadius < 10}>10 km</option>
                <option value="15" disabled={maxAllowedRadius < 15}>15 km</option>
                  <option value="20" disabled={maxAllowedRadius < 20}>20 km</option>
                <option value="30" disabled={maxAllowedRadius < 30}>30 km</option>
              </select>
              </label>
            </div>
          </div>
        </div>
        <div className="RM-no-matches-message">
          <p>{t('matches.no_matches') || 'No matches found for this route.'}</p>
          <p>{t('matches.adjust_radius') || 'Try adjusting the search radius or check back later.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="RM-route-matches-container">
      <div className="RM-route-matches-header">
        <h3>
          {t('matches.title') || 'Route Matches'} ({filteredMatches.length}/{matches.length})
        </h3>
        <div className="RM-route-matches-controls">
          <div className="RM-radius-control">
            <label>
              {t('matches.search_radius') || 'Search Radius'}:
              <select value={searchRadius} onChange={handleRadiusChange}>
                <option value="5">5 km</option>
                <option value="10" disabled={maxAllowedRadius < 10}>10 km</option>
                <option value="15" disabled={maxAllowedRadius < 15}>15 km</option>
                <option value="20" disabled={maxAllowedRadius < 20}>20 km</option>
                <option value="30" disabled={maxAllowedRadius < 30}>30 km</option>
              </select>
            </label>
          </div>
        </div>
      </div>
      
      {/* Filter Controls */}
      <div className="RM-filter-controls">
        <div className="RM-filter-section">
          <div className="RM-filter-options">
            <div className="RM-custom-filter-item">
              <Checkbox1
                id="filter-drivers"
                label={
                  <span className="RM-checkbox-label-content">
                    <FaCarSide className="RM-filter-icon driver" />
                    {t('matches.show_drivers') || 'Drivers'}
                  </span>
                }
                checked={filters.showDrivers}
                onChange={(e) => handleFilterChange('showDrivers', e.target.checked)}
              />
            </div>
            
            <div className="RM-custom-filter-item">
              <Checkbox1
                id="filter-passengers"
                label={
                  <span className="RM-checkbox-label-content">
                    <FaUser className="RM-filter-icon passenger" />
                    {t('matches.show_passengers') || 'Passengers'}
                  </span>
                }
                checked={filters.showPassengers}
                onChange={(e) => handleFilterChange('showPassengers', e.target.checked)}
              />
            </div>
            
            <div className="RM-custom-filter-item">
              <Checkbox1
                id="filter-packages"
                label={
                  <span className="RM-checkbox-label-content">
                    <FaBox className="RM-filter-icon package" />
                    {t('matches.show_packages') || 'Packages'}
                  </span>
                }
                checked={filters.showPackages}
                onChange={(e) => handleFilterChange('showPackages', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {filteredMatches.length === 0 ? (
        <div className="RM-no-matches-message">
          <p>{t('matches.no_filtered_matches') || 'No matches found with current filters.'}</p>
          <p>{t('matches.adjust_filters') || 'Try adjusting your filters or search radius.'}</p>
        </div>
      ) : (
        <div className="RM-route-matches-list">
          {filteredMatches.map((match) => (
            <div
              key={`${match.match_type}-${match.match_id}`}
              className={`RM-match-card ${match.match_type}-card`}
              onClick={() => handleMatchClick(match)}
            >
              <div className="RM-match-header">
                <div className="RM-match-user">
                  {match.avatar_url ? (
                    <img src={match.avatar_url} alt={match.username} className="RM-avatar" />
                  ) : (
                    <FaUser className="RM-avatar-placeholder" />
                  )}
                  <span className="RM-username">{match.username}</span>
                </div>
                <div className="RM-match-type">
                  {match.match_role === 'Driver' && <FaCarSide className="RM-role-icon driver" />}
                  {match.match_role === 'Passenger' && <FaUser className="RM-role-icon passenger" />}
                  {match.match_role === 'Package' && <FaBox className="RM-role-icon package" />}
                </div>
              </div>
              
              <div className="RM-match-route">
                <div className="RM-route-point">
                  <span>{t('posts.from')}: {showAddressInsteadOfCoords
                    ? addressData[`${match.match_id}-from`] || `${parseFloat(match.start_point_lat || 0).toFixed(4)}, ${parseFloat(match.start_point_lng || 0).toFixed(4)}`
                    : `${parseFloat(match.start_point_lat || 0).toFixed(4)}, ${parseFloat(match.start_point_lng || 0).toFixed(4)}`
                  }</span>
                </div>
                <div className="RM-route-point">
                  <span>{t('posts.to')}: {showAddressInsteadOfCoords
                    ? addressData[`${match.match_id}-to`] || `${parseFloat(match.end_point_lat || 0).toFixed(4)}, ${parseFloat(match.end_point_lng || 0).toFixed(4)}`
                    : `${parseFloat(match.end_point_lat || 0).toFixed(4)}, ${parseFloat(match.end_point_lng || 0).toFixed(4)}`
                  }</span>
                </div>
              </div>
              
              <div className="RM-match-details">
                <div className="RM-match-detail">
                  <span>{new Date(match.travel_date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="RM-match-detail">
                  <span>{match.travel_time ? match.travel_time.substring(0, 5) : ''}</span>
                </div>
                <div className="RM-match-detail">
                  <span>{parseFloat(match.distance_km || 0).toFixed(1)} km {t('posts.away')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}